/**
 * One **pair card** — the shape both halves of the Similar tab are made of after the
 * client round of 2 October 2026.
 *
 * What it replaces is worth writing down, because the replacement is the whole point. Both
 * blocks on that tab used to be one dense row per pair: two names, two class badges, a
 * similarity bar, an arrow, a number of points and a tag, all on one line that wrapped to
 * three at the dock's width. Every number in it was a *difference*, so a reader could see
 * that one side was 19 points behind and never see what either side actually reported.
 *
 * A card says the same thing in three registers, each of which can be read alone:
 *
 * 1. a header line naming both watersheds, their class, and how alike they are;
 * 2. the two adoption figures as two bars on one scale, the positive side in the positive
 *    green and the other neutral, with the programme average as a tick on both — so "19
 *    points behind" has a size and a direction a reader can see;
 * 3. one plain sentence saying which side reports less, and the tag.
 *
 * The card knows nothing about where its two sides came from: the contrast list hands it a
 * precomputed pair, the Most-alike block hands it the clicked watershed and one candidate.
 * That is deliberate — the two blocks answer the same question from different ends, and a
 * reader who has learned to read one has learned to read the other.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DimensionBars, type DimRow } from './charts/DimensionBars';
import {
  SIMILAR_OPPORTUNITY_COLOR,
  UC2_CATEGORY_COLORS,
  UC2_REST_COLOR,
} from './uc2Constants';

/** One watershed of a pair, as the card reads it. */
export interface PairSide {
  id: string;
  name: string;
  /**
   * The unit's own class — `positive` is the only one that changes the picture (it takes
   * the positive green).
   *
   * `IntelSimilarRow` does not carry one, so a Most-alike candidate's class is looked up
   * in the units rows the pane already holds rather than left `undefined` (client round
   * 2 Oct 2026). It had been left out as the honest answer to "the row does not say", but
   * the two blocks are now the same card: a watershed drawn green among the contrast pairs
   * and grey two blocks up, for the same verdict, teaches the reader that the green means
   * nothing. `undefined` remains what a caller passes when it genuinely cannot find out.
   */
  category?: string | null;
  /** Adoption of the chosen group, in percent of farmers; `null` for no survey value. */
  value: number | null;
}

export interface Uc2PairCardProps {
  a: PairSide;
  b: PairSide;
  /** How alike the two are, 0–100, already rounded. */
  similarityPct: number;
  /** `#uc2Practice`'s label, as the header and the chart print it. */
  practiceLabel: string;
  /** The same group lower-cased for mid-sentence use — a locale string, not a transform. */
  practiceLower: string;
  /** `a − b` in points; `null` when either side has no survey value for the group. */
  gap: number | null;
  /** The engine's own flag for this pair and this group. */
  learning: boolean;
  /** `summary.programme_means[group]` — the tick both bars are read against. */
  programmeMean?: number | null;
  /**
   * `uc2-contrast-W1-W2`, `uc2-similar-W2`: the card's own id. Its sentence and tag are
   * addressed as `uc2-pair-say-…`/`uc2-pair-tag-…` rather than as suffixes of this one,
   * so a block counting its cards with a `^=` selector counts cards and not their parts.
   */
  testId: string;
  /** The Compare button, on the Most-alike card; nothing on a contrast pair. */
  action?: ReactNode;
  /**
   * The plain note under the card (`Uc2Notes`), when the block passes one. It is the
   * caller's because the two blocks word it differently — a contrast pair says what the
   * pair is, the Most-alike block says it once for all three candidates.
   */
  note?: ReactNode;
}

/** The side's bar colour: the positive green for positive change, neutral for the rest. */
function colorOf(side: PairSide): string {
  return side.category === 'positive' ? UC2_CATEGORY_COLORS.positive! : UC2_REST_COLOR;
}

export function Uc2PairCard({
  a,
  b,
  similarityPct,
  practiceLabel,
  practiceLower,
  gap,
  learning,
  programmeMean,
  testId,
  action,
  note,
}: Uc2PairCardProps) {
  const { t } = useTranslation();

  const badge = (side: PairSide) =>
    side.category ? (
      <span
        className="uc2-class-badge"
        style={{ borderColor: UC2_CATEGORY_COLORS[side.category] ?? UC2_REST_COLOR }}
      >
        {t(`intel.uc2.category.${side.category}`, { defaultValue: side.category })}
      </span>
    ) : null;

  const rows: DimRow[] = [a, b].map((side) => ({
    id: side.id,
    label: side.name,
    value: side.value,
    unit: '%',
    reference: typeof programmeMean === 'number' ? programmeMean : null,
    color: colorOf(side),
  }));

  /**
   * The sentence. It is read off `gap` rather than off the two bars, because the gap is
   * what the engine stored and the two values are only what this run happened to serve
   * alongside it — a pair whose sides carry no adoption still has a gap worth a sentence.
   *
   * The points are **rounded before the direction is chosen**, the rule `#uc2Contrasts`
   * already followed with its arrow: a 0.4-point difference prints as "0 points", and a
   * sentence claiming a side reports less than the other while showing no difference at
   * all is the chart and the words disagreeing in front of the reader.
   */
  const points = gap == null ? null : Math.round(Math.abs(gap));
  const sentence =
    points == null
      ? t('intel.uc2.pairNoValue', { practice: practiceLower })
      : points === 0
        ? t('intel.uc2.pairSame', { a: a.name, b: b.name, practice: practiceLower })
        : t('intel.uc2.pairLess', {
            count: points,
            low: gap! > 0 ? b.name : a.name,
            high: gap! > 0 ? a.name : b.name,
            practice: practiceLower,
          });

  return (
    <li className="uc2-similar-row uc2-pair" data-testid={testId}>
      <p className="uc2-pair-head">
        <span className="uc2-similar-name">{a.name}</span>
        {badge(a)}
        <span className="uc2-pair-and">{t('intel.uc2.pairAnd')}</span>
        <span className="uc2-similar-name">{b.name}</span>
        {badge(b)}
        <span className="uc2-pair-alike">{t('intel.uc2.pairAlike', { pct: similarityPct })}</span>
      </p>
      <DimensionBars
        rows={rows}
        max={100}
        ariaLabel={t('intel.uc2.pairChart', { practice: practiceLabel })}
        referenceLabel={t('intel.uc2.chart.programmeMean')}
        // Both rows read against the same tick, so the words go under the chart once
        // rather than under each bar — ten cards' worth of them was thirty printings of
        // one fact, twice per chart in the same place.
        refCaption=""
      />
      {typeof programmeMean === 'number' ? (
        <ul className="chart-legend" data-testid={`uc2-pair-ref-${testId}`}>
          <li>
            <span className="swatch dim-swatch-ref" aria-hidden="true" />
            <span className="legend-label">{t('intel.uc2.chart.programmeMean')}</span>
          </li>
        </ul>
      ) : null}
      <p className="uc2-pair-say" data-testid={`uc2-pair-say-${testId}`}>
        {sentence}
      </p>
      {/* Layout round 2 Oct 2026: the tag and the Compare button are one row at the foot
          of the card, left-aligned with everything above them — as two loose children of
          a column they each took a centred line of their own. */}
      <p className="uc2-pair-foot">
        <span
          className="uc2-similar-tag"
          data-testid={`uc2-pair-tag-${testId}`}
          style={learning ? { borderColor: SIMILAR_OPPORTUNITY_COLOR } : undefined}
        >
          {t(learning ? 'intel.uc2.learningOpportunity' : 'intel.uc2.similarAdoption')}
        </span>
        {action}
      </p>
      {note}
    </li>
  );
}

export default Uc2PairCard;
